import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { User as SupabaseUser, Session } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { UserRole } from '../types/database';
import { logLogin, logLogout } from '../services/auditService';

// ============================================================================
// PDPO ROLE TYPES (NEW)
// ============================================================================

export type PdpoRoleCode =
  | 'DPO'           // Data Protection Officer - Level 1
  | 'CEO'           // Chief Executive Officer - Level 1
  | 'CFO'           // Chief Financial Officer - Level 1
  | 'LEGAL'         // Legal Counsel - Level 1/2
  | 'DEPT_HEAD'     // Department Head - Level 2
  | 'MANAGER'       // Manager - Level 2/3
  | 'SUPERVISOR'    // Supervisor - Level 3
  | 'STAFF';        // Staff Member - Level 4+

export interface PdpoRole {
  roleCode: PdpoRoleCode;
  roleName: string;
  hierarchyLevel: number;
  isCurrent: boolean;
}

// ============================================================================
// EXTENDED USER INTERFACE (UPDATED)
// ============================================================================

export interface User {
  id: string;
  organisation_id: string;
  auth_user_id?: string;
  email: string;
  role: UserRole;  // System role (sme_admin, sme_staff, etc.)
  name: string;
  job_title?: string;
  phone?: string;
  is_active: boolean;
  last_login_at?: string;
  created_at: string;
  updated_at: string;
  
  // NEW: PDPO roles from authority system
  pdpoRoles: PdpoRole[];
  primaryPdpoRole?: PdpoRole | null;
}

// ============================================================================
// AUTH CONTEXT TYPE
// ============================================================================

interface AuthContextType {
  user: SupabaseUser | null;
  userProfile: User | null;
  session: Session | null;
  loading: boolean;
  /**
   * Which product this company may open. Three states, and the difference
   * matters: `undefined` is still resolving, `null` is resolved to no answer,
   * and a string is the answer. Neither of the first two is permission — but
   * they are different screens, because showing an upgrade pitch to a paying
   * customer whose lookup merely failed would be worse than showing nothing.
   * See the Surfaces section of GO.md.
   */
  surface: string | null | undefined;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  fetchUserProfile: (authUserId: string) => Promise<User | null>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// ============================================================================
// Type for the Supabase join result from user_authorities + authority_roles
// ============================================================================

interface UserAuthorityRow {
  id: string;
  is_active: boolean;
  authority_roles: {
    role_code: string;
    role_name: string;
    hierarchy_level: number;
  } | null;
}

// ============================================================================
// AUTH PROVIDER
// ============================================================================

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<SupabaseUser | null>(null);
  const [userProfile, setUserProfile] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [surface, setSurface] = useState<string | null | undefined>(undefined);

  useEffect(() => {
    let subscription: { unsubscribe: () => void } | null = null;

    const initAuth = async () => {
      try {
        const { data: { session: currentSession } } = await supabase.auth.getSession();
        setSession(currentSession);
        setUser(currentSession?.user ?? null);

        if (currentSession?.user) {
          await fetchUserProfile(currentSession.user.id);
        }

        setLoading(false);

        const { data: authListener } = supabase.auth.onAuthStateChange(
          (_event, session) => {
            (async () => {
              try {
                setSession(session);
                setUser(session?.user ?? null);

                if (session?.user) {
                  await fetchUserProfile(session.user.id);
                } else {
                  setUserProfile(null);
                }
              } catch (error) {
                console.error('[AuthContext] Error in auth state change:', error);
              }
            })();
          }
        );

        subscription = authListener.subscription;
      } catch (error) {
        console.error('[AuthContext] Error initializing auth:', error);
        setLoading(false);
      }
    };

    initAuth();

    return () => {
      if (subscription) {
        subscription.unsubscribe();
      }
    };
  }, []);

  // ============================================================================
  // FETCH USER PROFILE WITH PDPO ROLES (UPDATED)
  // ============================================================================

  const fetchUserProfile = async (authUserId: string): Promise<User | null> => {
    try {
      // 1. Fetch base user profile
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('*')
        .eq('auth_user_id', authUserId)
        .maybeSingle();

      if (userError) {
        console.error('[AuthContext] Error fetching user profile:', userError);
        return null;
      }

      if (!userData) {
        console.log('[AuthContext] No user profile found');
        setUserProfile(null);
        return null;
      }

      // 2. Fetch PDPO roles for this user (NEW)
      const { data: pdpoData, error: pdpoError } = await supabase
        .from('user_authorities')
        .select(`
          id,
          is_active,
          authority_roles (
            role_code,
            role_name,
            hierarchy_level
          )
        `)
        .eq('user_id', userData.id)
        .eq('is_active', true)
        .lte('valid_from', new Date().toISOString())
        .or(`valid_until.is.null,valid_until.gte.${new Date().toISOString()}`);

      if (pdpoError) {
        console.error('[AuthContext] Error fetching PDPO roles:', pdpoError);
        // Continue without PDPO roles rather than failing completely
      }

      // 3. Map PDPO roles (NEW)
      const pdpoRoles: PdpoRole[] = ((pdpoData || []) as unknown as UserAuthorityRow[])
        .filter((ua) => ua.authority_roles) // Ensure join succeeded
        .map((ua) => ({
          roleCode: ua.authority_roles!.role_code as PdpoRoleCode,
          roleName: ua.authority_roles!.role_name,
          hierarchyLevel: ua.authority_roles!.hierarchy_level,
          isCurrent: true,
        }));

      console.log('[AuthContext] Found PDPO roles:', pdpoRoles.length);

      // 4. Find primary PDPO role (lowest hierarchy level = highest authority) (NEW)
      const primaryPdpoRole = pdpoRoles.length > 0
        ? [...pdpoRoles].sort((a, b) => a.hierarchyLevel - b.hierarchyLevel)[0]
        : null;

      if (primaryPdpoRole) {
        console.log('[AuthContext] Primary PDPO role:', primaryPdpoRole.roleName, `(Level ${primaryPdpoRole.hierarchyLevel})`);
      } else {
        console.log('[AuthContext] No PDPO role assigned - defaulting to staff permissions');
      }

      // 5. Create extended user profile (NEW)
      const profile: User = {
        ...userData,
        pdpoRoles,
        primaryPdpoRole,
      };

      setUserProfile(profile);

      // Asked of the database rather than read off the org row here, so the two
      // apps share one definition of the gate instead of two that drift.
      supabase
        .rpc('organisation_surface')
        .then(({ data, error: surfaceError }) => {
          if (surfaceError) {
            // Unknown is not permission. Leaving it null keeps the user out of
            // the main app rather than letting a failed lookup admit them.
            console.error('[AuthContext] Could not resolve surface:', surfaceError);
            setSurface(null);
            return;
          }
          setSurface(typeof data === 'string' ? data : null);
        });

      return profile;
    } catch (error) {
      console.error('[AuthContext] Exception fetching user profile:', error);
      return null;
    }
  };

  // ============================================================================
  // AUTH FUNCTIONS
  // ============================================================================

  const signIn = async (email: string, password: string) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        console.error('[AuthContext] Login failed:', error);
        throw error;
      }

      if (!data.user) {
        throw new Error('No user data returned from login');
      }

      setUser(data.user);
      setSession(data.session);

      const profile = await fetchUserProfile(data.user.id);

      if (profile) {
        // Update last_login_at timestamp
        supabase
          .from('users')
          .update({ last_login_at: new Date().toISOString() })
          .eq('id', profile.id)
          .then(({ error: updateErr }) => {
            if (updateErr) {
              console.error('[AuthContext] Failed to update last_login_at:', updateErr);
            }
          });

        await logLogin(
          profile.organisation_id,
          profile.id,
          email,
          { success: true }
        ).catch(err => console.error('[AuthContext] Failed to log login:', err));
      }
    } catch (error) {
      console.error('[AuthContext] Sign in error:', error);
      throw error;
    }
  };

  const signOut = async () => {
    if (userProfile) {
      await logLogout(
        userProfile.organisation_id,
        userProfile.id,
        userProfile.email
      ).catch(err => console.error('[AuthContext] Failed to log logout:', err));
    }

    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    setUserProfile(null);
    setSurface(undefined);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        userProfile,
        session,
        loading,
        surface,
        signIn,
        signOut,
        fetchUserProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

// ============================================================================
// CUSTOM HOOK
// ============================================================================

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

// ============================================================================
// HELPER FUNCTIONS (NEW)
// ============================================================================

/**
 * Check if user is a "power user" (management/leadership)
 * Power users see full PDPO console with all features
 */
export function isPowerUser(user: User | null): boolean {
  if (!user?.primaryPdpoRole) return false;
  
  const powerRoles: PdpoRoleCode[] = ['DPO', 'CEO', 'CFO', 'LEGAL', 'DEPT_HEAD', 'MANAGER'];
  return powerRoles.includes(user.primaryPdpoRole.roleCode);
}

/**
 * Check if user is a staff member (limited access)
 * Staff users see "light version" with only their tasks
 */
export function isStaffUser(user: User | null): boolean {
  // Default to staff if no PDPO role assigned
  if (!user?.primaryPdpoRole) return true;
  
  return user.primaryPdpoRole.roleCode === 'STAFF' || 
         user.primaryPdpoRole.hierarchyLevel >= 4;
}

/**
 * Check if user has supervisor-level access or higher
 */
export function isSupervisorOrHigher(user: User | null): boolean {
  if (!user?.primaryPdpoRole) return false;
  return user.primaryPdpoRole.hierarchyLevel <= 3;
}

/**
 * Check if user has a specific minimum authority level
 * @param user - User object
 * @param level - Required level (1 = highest, 4+ = staff)
 * @returns true if user has this level or higher (lower number)
 */
export function hasMinimumLevel(user: User | null, level: number): boolean {
  if (!user?.primaryPdpoRole) return false;
  return user.primaryPdpoRole.hierarchyLevel <= level;
}

/**
 * Check if user has specific PDPO role
 */
export function hasPdpoRole(user: User | null, roleCode: PdpoRoleCode): boolean {
  if (!user?.primaryPdpoRole) return false;
  return user.primaryPdpoRole.roleCode === roleCode;
}

/**
 * Check if user has any of the specified PDPO roles
 */
export function hasAnyPdpoRole(user: User | null, roleCodes: PdpoRoleCode[]): boolean {
  if (!user?.primaryPdpoRole) return false;
  return roleCodes.includes(user.primaryPdpoRole.roleCode);
}

/**
 * Get user's PDPO role display name
 */
export function getPdpoRoleName(user: User | null): string {
  return user?.primaryPdpoRole?.roleName || 'Staff Member';
}

/**
 * Get user's authority level (1-4+)
 */
export function getAuthorityLevel(user: User | null): number | null {
  return user?.primaryPdpoRole?.hierarchyLevel || null;
}